import React,{useState,useEffect} from 'react';
import SafeIcon from '../common/SafeIcon';
import HeaderLogo from './HeaderLogo';
import * as FiIcons from 'react-icons/fi';
import {motion,AnimatePresence} from 'framer-motion';
import {useAuth} from '../context/AuthContext';

const {FiMenu,FiX,FiChevronDown,FiLogOut,FiUsers,FiCalendar,FiUser,FiBarChart2,FiMessageSquare}=FiIcons;

const SUBTITLE='Entrenamiento de Taekwondo';

export default function Navbar({
  categories,
  activeTab,
  setActiveTab,
  onLogoClick,
  view,
  setView
}) {
  const [isOpen,setIsOpen]=useState(false);
  const [isScrolled,setIsScrolled]=useState(false);
  const [isTechOpen,setIsTechOpen]=useState(false);
  const [isAdminOpen,setIsAdminOpen]=useState(false);
  const {logout,isAdmin,canManageEvents}=useAuth();

  useEffect(()=> {
    const handleScroll=()=> setIsScrolled(window.scrollY>20);
    window.addEventListener('scroll',handleScroll);
    return ()=> window.removeEventListener('scroll',handleScroll);
  },[]);

  const handleLinkClick=(viewName)=> {
    setView(viewName);
    setIsOpen(false);
    window.scrollTo({top:0,behavior:'smooth'});
  };

  const handleTechClick=(catId)=> {
    setActiveTab(catId);
    setView('category');
    setIsOpen(false);
    setIsTechOpen(false);
  };

  const handleAdminClick=(viewName)=> {
    handleLinkClick(viewName);
    setIsAdminOpen(false);
  };

  // Admin y profesor tienen más opciones y necesitan más ancho antes de mostrar el menú completo.
  const desktopMenuClass=canManageEvents ? 'hidden xl:flex' : 'hidden lg:flex';
  const mobileOnlyClass=canManageEvents ? 'xl:hidden' : 'lg:hidden';

  const techSubmenu=[
    {id:'basicos',label:'Básicos'},
    {id:'secuencias',label:'Secuencias'},
    {id:'formas',label:'Formas'},
    {id:'defensa_personal',label:'Defensa personal'}
  ];

  // Opciones de administración según el rol; en escritorio van agrupadas en un menú.
  const adminItems=[
    {id:'usuarios',label:'Usuarios',icon:FiUsers,show:isAdmin},
    {id:'metricas',label:'Métricas',icon:FiBarChart2,show:isAdmin},
    {id:'comentarios',label:'Comentarios',icon:FiMessageSquare,show:isAdmin},
    {id:'admin-eventos',label:'Admin eventos',icon:FiCalendar,show:canManageEvents}
  ].filter(item=> item.show);
  const isAdminView=adminItems.some(item=> item.id===view);

  return (
    <nav className="fixed top-0 left-0 right-0 z-[60] flex justify-center items-center px-4 pt-4">
      <div className={`max-w-7xl w-full h-20 transition-all duration-500 rounded-[1.5rem] border overflow-visible ${isScrolled?'bg-zinc-900/70 border-white/10 shadow-2xl backdrop-blur-2xl':'bg-white/5 border-white/5 backdrop-blur-md'}`}>
        <div className="h-full px-6 md:px-8 flex items-center justify-between">
          <button
            type="button"
            onClick={onLogoClick}
            aria-label="Ir al inicio"
            className="flex items-center gap-3 cursor-pointer group"
          >
            <HeaderLogo className="h-20 w-20 md:h-24 md:w-24 object-contain transition-transform duration-300 group-hover:scale-110" />
            <div className="hidden sm:flex flex-col items-start justify-center whitespace-nowrap text-left">
              <span className="font-brush text-2xl md:text-3xl font-normal leading-none text-white [text-shadow:_0_2px_6px_rgba(0,0,0,0.5)]">
                DOJANG DIGITAL
              </span>
              {/* w-0 + min-w-full: el subtítulo no define el ancho, solo ocupa el del título y reparte sus letras */}
              <span
                aria-label="Entrenamiento de Taekwondo"
                className="mt-1 flex w-0 min-w-full justify-between text-[8px] md:text-[10px] font-black uppercase text-white/70"
              >
                {SUBTITLE.split('').map((char,index)=> (
                  <span key={index} aria-hidden="true">{char===' ' ? '\u00A0' : char}</span>
                ))}
              </span>
            </div>
            <span className="sr-only">Dojang Digital Tae Kwon Do</span>
          </button>

          <div className={`${desktopMenuClass} items-center space-x-1`}>
            <NavItem label="Inicio" isActive={view==='home'} onClick={onLogoClick} />

            <div
              className="relative"
              onMouseEnter={()=> setIsTechOpen(true)}
              onMouseLeave={()=> setIsTechOpen(false)}
            >
              <button className={`px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest flex items-center gap-1.5 ${view==='category'?'bg-red-600 text-white':'text-zinc-100 hover:text-white hover:bg-white/15 [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]'}`}>
                Técnicas <SafeIcon icon={FiChevronDown} />
              </button>

              <AnimatePresence>
                {isTechOpen&&(
                  <motion.div
                    initial={{opacity:0,y:10}}
                    animate={{opacity:1,y:0}}
                    exit={{opacity:0,y:10}}
                    className="absolute top-full left-0 mt-2 w-48 bg-zinc-950/95 backdrop-blur-xl border border-white/20 rounded-2xl p-2 shadow-2xl"
                  >
                    {techSubmenu.map(item=>(
                      <button
                        key={item.id}
                        onClick={()=> handleTechClick(item.id)}
                        className="w-full text-left px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-wider text-zinc-100 hover:text-white hover:bg-white/15 [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]"
                      >
                        {item.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <NavItem
              label={<div className="flex items-center gap-2"><SafeIcon icon={FiCalendar} /> Eventos</div>}
              isActive={view==='eventos'}
              onClick={()=> handleLinkClick('eventos')}
            />
            <NavItem
              label="Entrenamiento"
              isActive={view==='entrenamiento'}
              onClick={()=> handleLinkClick('entrenamiento')}
            />

            {adminItems.length>1&&(
              <div
                className="relative"
                onMouseEnter={()=> setIsAdminOpen(true)}
                onMouseLeave={()=> setIsAdminOpen(false)}
              >
                <button className={`px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest flex items-center gap-1.5 ${isAdminView?'bg-red-600 text-white':'text-zinc-100 hover:text-white hover:bg-white/15 [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]'}`}>
                  Administración <SafeIcon icon={FiChevronDown} />
                </button>

                <AnimatePresence>
                  {isAdminOpen&&(
                    <motion.div
                      initial={{opacity:0,y:10}}
                      animate={{opacity:1,y:0}}
                      exit={{opacity:0,y:10}}
                      className="absolute top-full right-0 mt-2 w-52 bg-zinc-950/95 backdrop-blur-xl border border-white/20 rounded-2xl p-2 shadow-2xl"
                    >
                      {adminItems.map(item=>(
                        <button
                          key={item.id}
                          onClick={()=> handleAdminClick(item.id)}
                          className={`w-full flex items-center gap-3 text-left px-4 py-3 rounded-xl text-[11px] font-black uppercase tracking-wider ${view===item.id?'bg-red-600 text-white':'text-zinc-100 hover:text-white hover:bg-white/15 [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]'}`}
                        >
                          <SafeIcon icon={item.icon} /> {item.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {adminItems.length===1&&(
              <NavItem
                label="Admin eventos"
                isActive={view==='admin-eventos'}
                onClick={()=> handleLinkClick('admin-eventos')}
              />
            )}

            <div className="h-4 w-px bg-white/10 mx-2" />

            <button
              onClick={()=> handleLinkClick('perfil')}
              title="Mi perfil"
              aria-label="Mi perfil"
              className={`p-2 rounded-full ${view==='perfil'?'bg-red-600 text-white':'text-zinc-100 hover:text-white'}`}
            >
              <SafeIcon icon={FiUser} className="w-4 h-4" />
            </button>

            <button onClick={logout} title="Cerrar sesión" aria-label="Cerrar sesión" className="p-2 text-zinc-200 hover:text-red-400">
              <SafeIcon icon={FiLogOut} className="w-4 h-4" />
            </button>
          </div>

          <div className={`${mobileOnlyClass} flex items-center gap-1`}>
            <button
              onClick={()=> handleLinkClick('perfil')}
              title="Mi perfil"
              aria-label="Mi perfil"
              className={`p-2 rounded-full ${view==='perfil'?'bg-red-600 text-white':'text-zinc-100 hover:text-white'}`}
            >
              <SafeIcon icon={FiUser} className="w-5 h-5" />
            </button>
            <button onClick={logout} title="Cerrar sesión" aria-label="Cerrar sesión" className="p-2 text-zinc-200 hover:text-red-400">
              <SafeIcon icon={FiLogOut} className="w-5 h-5" />
            </button>
            <div className="h-5 w-px bg-white/15 mx-1" />
            <button onClick={()=> setIsOpen(!isOpen)} aria-label="Menú" className="p-2 text-zinc-100">
              <SafeIcon icon={isOpen?FiX:FiMenu} className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen&&(
          <motion.div
            initial={{opacity:0,y:-20}}
            animate={{opacity:1,y:0}}
            exit={{opacity:0,y:-20}}
            className={`${mobileOnlyClass} absolute top-28 left-4 right-4 max-h-[calc(100vh-8rem)] overflow-y-auto bg-zinc-950/95 backdrop-blur-3xl rounded-[2.5rem] border border-white/10 p-8 shadow-2xl`}
          >
            <div className="flex flex-col space-y-2">
              <MobileNavItem label="Inicio" isActive={view==='home'} onClick={onLogoClick} />
              <MobileNavItem
                label="Básicos"
                isActive={view==='category'&&activeTab==='basicos'}
                onClick={()=> handleTechClick('basicos')}
              />
              <MobileNavItem
                label="Secuencias"
                isActive={view==='category'&&activeTab==='secuencias'}
                onClick={()=> handleTechClick('secuencias')}
              />
              <MobileNavItem
                label="Formas"
                isActive={view==='category'&&activeTab==='formas'}
                onClick={()=> handleTechClick('formas')}
              />
              <MobileNavItem
                label="Defensa personal"
                isActive={view==='category'&&activeTab==='defensa_personal'}
                onClick={()=> handleTechClick('defensa_personal')}
              />
              <MobileNavItem label="Eventos" isActive={view==='eventos'} onClick={()=> handleLinkClick('eventos')} />
              <MobileNavItem label="Entrenamiento" isActive={view==='entrenamiento'} onClick={()=> handleLinkClick('entrenamiento')} />

              {isAdmin&&(
                <MobileNavItem
                  label="Administrar usuarios"
                  isActive={view==='usuarios'}
                  onClick={()=> handleLinkClick('usuarios')}
                />
              )}

              {isAdmin&&(
                <MobileNavItem
                  label="Métricas"
                  isActive={view==='metricas'}
                  onClick={()=> handleLinkClick('metricas')}
                />
              )}

              {isAdmin&&(
                <MobileNavItem
                  label="Comentarios"
                  isActive={view==='comentarios'}
                  onClick={()=> handleLinkClick('comentarios')}
                />
              )}

              {canManageEvents&&(
                <MobileNavItem
                  label="Administrar eventos"
                  isActive={view==='admin-eventos'}
                  onClick={()=> handleLinkClick('admin-eventos')}
                />
              )}

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

function NavItem({label,isActive,onClick}) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all ${isActive?'bg-red-600 text-white shadow-lg shadow-red-600/20':'text-zinc-100 hover:text-white hover:bg-white/15 [text-shadow:_0_1px_4px_rgba(0,0,0,0.8)]'}`}
    >
      {label}
    </button>
  );
}

function MobileNavItem({label,isActive,onClick}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-4 rounded-2xl font-black uppercase tracking-widest text-xs ${isActive?'bg-red-600 text-white':'text-zinc-100 hover:text-white'}`}
    >
      {label}
    </button>
  );
}